import React from 'react'
import { cn } from '../../../../_shared/lib'

export interface UsecaseTableUnitProps {
  children: React.ReactNode
  position?: 'top-right' | 'bottom-right'
}

const UsecaseTableUnit: React.FC<UsecaseTableUnitProps> = ({ children }) => {
  return <div className="flex items-end justify-end">{children}</div>
}

export interface UsecaseTableProps {
  children: React.ReactNode
}

export interface UsecaseTableTitleProps {
  children: React.ReactNode
}

export interface UsecaseTableHeadProps {
  children: React.ReactNode
}

export interface UsecaseTableBodyProps {
  children: React.ReactNode
}

export interface UsecaseTableRowProps {
  children: React.ReactNode
}

export type TableCellType = 'text' | 'currency' | 'number' | 'percentage' | 'date'

export interface UsecaseTableCellProps {
  children: React.ReactNode
  type: TableCellType
  component?: 'th' | 'td'
}

// Title Component
const UsecaseTableTitle: React.FC<UsecaseTableTitleProps> = ({ children }) => {
  return <h6 className="text-lg font-semibold text-gray-600 mb-2">{children}</h6>
}

// TableHead Component
const UsecaseTableHead: React.FC<UsecaseTableHeadProps> = ({ children }) => {
  return <thead className="bg-gray-100 text-gray-900 font-bold">{children}</thead>
}

// TableBody Component
const UsecaseTableBody: React.FC<UsecaseTableBodyProps> = ({ children }) => {
  return <tbody>{children}</tbody>
}

// TableRow Component
const UsecaseTableRow: React.FC<UsecaseTableRowProps> = ({ children }) => {
  return <tr>{children}</tr>
}

// TableCell Component
const UsecaseTableCell: React.FC<UsecaseTableCellProps> = ({ children, type = 'text', component = 'td' }) => {
  const getTextAlign = (cellType: TableCellType): string => {
    switch (cellType) {
      case 'text':
      case 'currency':
        return 'text-left'
      case 'number':
      case 'percentage':
      case 'date':
        return 'text-right'
      default:
        return 'text-left'
    }
  }

  const Component = component

  return (
    <Component
      className={cn(
        'border border-gray-300 p-2 text-sm',
        component === 'th' ? 'text-left' : getTextAlign(type),
        component === 'th' && 'font-bold min-w-[150px]',
        component === 'td' && 'font-normal min-w-[100px]',
      )}
    >
      {children}
    </Component>
  )
}

// Main Component
const UsecaseTable: React.FC<UsecaseTableProps> & {
  Title: typeof UsecaseTableTitle
  Head: typeof UsecaseTableHead
  Body: typeof UsecaseTableBody
  Row: typeof UsecaseTableRow
  Cell: typeof UsecaseTableCell
  Unit: typeof UsecaseTableUnit
} = ({ children }) => {
  // Separate Title and Unit from Table components
  const childrenArray = React.Children.toArray(children)
  const titleElement = childrenArray.find((child) => React.isValidElement(child) && child.type === UsecaseTableTitle)
  const unitElement = childrenArray.find((child) => React.isValidElement(child) && child.type === UsecaseTableUnit)
  const tableElements = childrenArray.filter((child) => React.isValidElement(child) && child.type !== UsecaseTableTitle && child.type !== UsecaseTableUnit)

  return (
    <div className="relative max-w-full flex flex-col">
      {titleElement}
      <div className="w-fit min-w-[300px] max-w-full relative overflow-x-auto">
        {unitElement}
        <table className="border-collapse text-sm">{tableElements}</table>
      </div>
    </div>
  )
}

// Attach sub-components
UsecaseTable.Title = UsecaseTableTitle
UsecaseTable.Head = UsecaseTableHead
UsecaseTable.Body = UsecaseTableBody
UsecaseTable.Row = UsecaseTableRow
UsecaseTable.Cell = UsecaseTableCell
UsecaseTable.Unit = UsecaseTableUnit

export { UsecaseTable }
